/**
 * 68.41, layout C's ring (ui/commandLayout.ts): F's or G's wedges round a point (the crosshair on the ground, the cursor at
 * the table); a wedge with ▸ shows its list beside the ring as soon as it is lit. An entry is picked three ways, all on
 * together (the owner: "pick by all"): its number key while the ring is held (`pickNumber`), the mouse onto the list then
 * release or click (`move`, `click`), or the wheel stepping the list then release (`wheel`). A release on a lit wedge
 * gives its row (the first, until one is chosen); in the centre, nothing (the host's contextual order on the ground, as
 * today's). The wedge follows the pointer's angle inside the ring and holds once the pointer is out past it or on its
 * list, so heading for the list never changes the wedge.
 *
 * The same face as today's OrderRadial (`open`, `show`, `move`, `close`, `dispose`), so the host's release paths stay
 * one; `close` answers in today's verbs (core/orderMenu.ts). The DOM is drawn only where there is a document (the tests
 * drive the logic bare). Cost: one absolutely-placed div, its innerHTML redrawn on a change (a move, a wheel step), none
 * while it is shut.
 */
import { iconSvg } from './commandIcons';
import { wedgeDefault, type CommandEntry, type CommandWedge } from './commandLayout';
import type { RadialOptions } from './orderRadial';

export const RING = { rIn: 34, rOut: 104, rowH: 25, listW: 186, gap: 18 };
/** A wheel notch (pixels, or a line × 40): a trackpad's small deltas add up to one. */
const NOTCH = 40;

/** The ring's options: today's (dims, banner, footer, the hub), plus counts on entries ("verb/sub" → its badge). */
export interface RingOptions extends RadialOptions { badges?: ReadonlyMap<string, string> }

export class CommandRing {
  open = false;
  private x = 0; private y = 0;
  private px = 0; private py = 0;
  private wedge: number | null = null;
  private row = 0;
  private rowBy: 'default' | 'mouse' | 'wheel' = 'default';
  private wheelAcc = 0;
  private picked: CommandEntry | null = null;
  private opts: RingOptions = {};
  private el: HTMLDivElement | null = null;

  constructor(readonly wedges: readonly CommandWedge[], private screen: () => { w: number; h: number } = () => ({ w: typeof window !== 'undefined' ? window.innerWidth : 1280, h: typeof window !== 'undefined' ? window.innerHeight : 800 })) {
    if (typeof document !== 'undefined') {
      this.el = document.createElement('div');
      this.el.style.cssText = 'position:fixed; left:0; top:0; width:0; height:0; pointer-events:none; z-index:30; display:none; font:12px system-ui, sans-serif;';
      document.body.appendChild(this.el);
    }
  }

  dispose(): void { this.el?.remove(); this.el = null; }

  show(x: number, y: number, opts: RingOptions = {}): void {
    this.x = x; this.y = y; this.px = x; this.py = y; this.open = true;
    this.wedge = null; this.row = 0; this.rowBy = 'default'; this.wheelAcc = 0; this.picked = null; this.opts = opts;
    if (this.el) { this.el.style.display = ''; this.el.style.left = `${x}px`; this.el.style.top = `${y}px`; }
    this.draw();
  }

  /** A list less the entries hidden this time (RadialOptions.hidden). */
  private shown(list: CommandEntry[]): CommandEntry[] {
    const hide = this.opts.hidden;
    return hide?.size ? list.filter(e => !hide.has(`${e.verb}/${e.sub}`)) : list;
  }

  /** The lit wedge's list (null: none lit, it has none, or it is dimmed). */
  private list(): CommandEntry[] | null {
    if (this.wedge === null) return null;
    const w = this.wedges[this.wedge];
    return w.list && !this.opts.disabled?.has(w.id) ? this.shown(w.list) : null;
  }

  /** The list's place beside the ring (screen offsets from the centre): on the wedge's side, level with it, flipped if off the screen. */
  listRect(): { x0: number; top: number; w: number; h: number } | null {
    const list = this.list();
    if (!list || this.wedge === null) return null;
    const n = this.wedges.length, a = -Math.PI / 2 + this.wedge * 2 * Math.PI / n;
    const { w: W } = this.screen();
    let right = Math.cos(a) >= -0.05;
    const ay = (RING.rOut + 16) * Math.sin(a), half = (list.length * RING.rowH) / 2;
    const top = Math.max(-RING.rOut, Math.min(RING.rOut - 2 * half, ay - half));
    if (right && this.x + RING.rOut + RING.gap + RING.listW > W - 8) right = false;
    else if (!right && this.x - RING.rOut - RING.gap - RING.listW < 8) right = true;
    return { x0: right ? RING.rOut + RING.gap : -RING.rOut - RING.gap - RING.listW, top, w: RING.listW, h: list.length * RING.rowH };
  }

  /** The pointer moved (screen point): the wedge by its angle inside the ring; on the list, its row. */
  move(px: number, py: number): void {
    if (!this.open) return;
    this.px = px; this.py = py;
    const dx = px - this.x, dy = py - this.y, d = Math.hypot(dx, dy);
    const rect = this.listRect();
    if (rect && dx >= rect.x0 && dx <= rect.x0 + rect.w && dy >= rect.top && dy <= rect.top + rect.h) {
      this.row = Math.min(this.list()!.length - 1, Math.floor((dy - rect.top) / RING.rowH)); this.rowBy = 'mouse';
    } else if (d < RING.rIn) {
      this.wedge = null; this.row = 0; this.rowBy = 'default';
    } else if (d <= RING.rOut || this.wedge === null) {
      const n = this.wedges.length, step = 2 * Math.PI / n;
      const w = ((Math.round((Math.atan2(dy, dx) + Math.PI / 2) / step) % n) + n) % n;
      if (w !== this.wedge) { this.wedge = w; this.row = 0; this.rowBy = 'default'; this.wheelAcc = 0; }
    }
    this.draw();
  }

  /** The wheel (a delta and its mode): steps the lit list a row a notch. True when it was the ring's. */
  wheel(deltaY: number, deltaMode = 0): boolean {
    const list = this.list();
    if (!this.open || !list) return this.open;
    this.wheelAcc += deltaMode === 1 ? deltaY * NOTCH : deltaY;
    if (Math.abs(this.wheelAcc) < NOTCH) return true;
    const step = Math.sign(this.wheelAcc);
    this.wheelAcc = 0;
    this.row = Math.max(0, Math.min(list.length - 1, this.row + step)); this.rowBy = 'wheel';
    this.draw();
    return true;
  }

  /** A number key (1 …) while held: that row of the lit list, picked (true), or nothing (a row past the list's end, none lit). */
  pickNumber(n: number): boolean {
    const list = this.list();
    if (!this.open || !list || n < 1 || n > list.length) return false;
    this.picked = list[n - 1];
    return true;
  }

  /** A click: what a release would give, picked (true), or nothing (in the centre, or a dimmed wedge). */
  click(): boolean {
    const x = this.current();
    if (!this.open || !x) return false;
    this.picked = x;
    return true;
  }

  /** What a release gives now: the picked entry, the lit list's row, or the lit wedge's own; null in the centre or dimmed. */
  current(): CommandEntry | null {
    if (this.picked) return this.picked;
    if (this.wedge === null) return null;
    const w = this.wedges[this.wedge];
    if (this.opts.disabled?.has(w.id)) return null;
    const list = w.list ? this.shown(w.list) : null;
    return list?.length ? list[Math.min(this.row, list.length - 1)] : wedgeDefault(w);
  }

  /** Close: the entry chosen in today's verbs, or null (the centre, a dimmed wedge). */
  close(): { verb: string; sub: string | null } | null {
    if (!this.open) return null;
    const x = this.current();
    this.open = false; this.picked = null;
    if (this.el) this.el.style.display = 'none';
    return x ? { verb: x.verb, sub: x.sub } : null;
  }

  private draw(): void {
    if (!this.el || !this.open) return;
    const n = this.wedges.length, o = this.opts, R_IN = RING.rIn, R_OUT = RING.rOut;
    const hub = o.hub
      ? `<div style="color:${this.wedge === null ? '#e6d070' : '#9aa0b4'}; line-height:1.05;">${o.hub.icon ? iconSvg(o.hub.icon, { size: 22 }) : ''}<div style="font-size:9px;">${o.hub.label}</div></div>`
      : (this.wedge === null ? 'release<br>to cancel' : '');
    const parts: string[] = [
      `<div style="position:absolute; left:${-R_OUT}px; top:${-R_OUT}px; width:${2 * R_OUT}px; height:${2 * R_OUT}px; border-radius:50%; background:rgba(12,12,24,0.55); border:1px solid rgba(255,255,255,0.15);"></div>`,
      `<div style="position:absolute; left:${-R_IN}px; top:${-R_IN}px; width:${2 * R_IN}px; height:${2 * R_IN}px; border-radius:50%; background:rgba(12,12,24,0.75); border:1px solid ${o.hub && this.wedge === null ? 'rgba(230,208,112,0.7)' : 'rgba(255,255,255,0.2)'}; display:flex; align-items:center; justify-content:center; color:#9aa0b4; font-size:10px; text-align:center;">${hub}</div>`,
    ];
    if (o.banner) parts.push(`<div style="position:absolute; left:-120px; top:${-R_OUT - 24}px; width:240px; text-align:center; color:#e9eef5; font-size:10px; letter-spacing:0.12em; text-shadow:0 1px 3px #000;">${o.banner}</div>`);
    if (o.footer) parts.push(`<div style="position:absolute; left:-120px; top:${R_OUT + 8}px; width:240px; text-align:center; color:#c8ccd8; font-size:10px; text-shadow:0 1px 3px #000;">${o.footer}</div>`);
    this.wedges.forEach((w, i) => {
      const a = -Math.PI / 2 + i * 2 * Math.PI / n, r = (R_IN + R_OUT) / 2;
      const x = Math.cos(a) * r, y = Math.sin(a) * r;
      const lit = i === this.wedge, dim = !!o.disabled?.has(w.id);
      const badge = w.entry ? o.badges?.get(`${w.entry.verb}/${w.entry.sub}`) : undefined;
      parts.push(`<div style="position:absolute; left:${x - 40}px; top:${y - 22}px; width:80px; text-align:center; color:${lit ? '#ffffff' : '#c8ccd8'}; opacity:${dim ? 0.35 : 1}; ${lit ? 'filter:drop-shadow(0 0 5px #6e9ec2);' : ''}"><div style="display:flex; justify-content:center; line-height:1;">${iconSvg(w.icon, { size: lit ? 30 : 26, title: w.label })}</div><div style="font-size:10px;">${w.label}${w.list ? ' ▸' : ''}${badge ? ` <span style="color:#e6d070;">${badge}</span>` : ''}</div></div>`);
    });
    const rect = this.listRect(), list = this.list();
    if (rect && list) {
      list.forEach((x, i) => {
        const lit = i === this.row, badge = o.badges?.get(`${x.verb}/${x.sub}`);
        parts.push(`<div style="position:absolute; left:${rect.x0}px; top:${rect.top + i * RING.rowH}px; width:${rect.w}px; height:${RING.rowH - 2}px; display:flex; align-items:center; gap:6px; padding:0 8px; box-sizing:border-box; border-radius:6px; background:${lit ? 'rgba(110,158,194,0.9)' : 'rgba(12,12,24,0.85)'}; color:#ffffff; font-size:11px;"><span style="color:${lit ? '#fff' : '#8a92a4'}; font-size:10px; width:9px;">${i + 1}</span>${iconSvg(x.icon, { size: 15 })}<span style="flex:1;">${x.label}</span>${badge ? `<span style="color:#e6d070; font-size:10px;">${badge}</span>` : ''}</div>`);
      });
      parts.push(`<div style="position:absolute; left:${rect.x0}px; top:${rect.top + rect.h + 2}px; width:${rect.w}px; color:#8a92a4; font-size:9px; text-align:center; text-shadow:0 1px 2px #000;">1–${list.length} · wheel · point · release picks</div>`);
    }
    this.el.innerHTML = parts.join('');
  }
}
